import React from "react";
import { motion } from "framer-motion";
import image551 from "../assets/image551.png";

const PharmaConnect = () => {
  return (
    <div className="min-h-screen w-full font-sans overflow-x-hidden">
      {/* Hero Section */}
   <section className="bg-[#d2e5e4] text-[#3d302c] h-[60vh] md:h-[48vh] flex flex-col justify-center items-center text-center px-4">
  <motion.h1
    className="text-4xl sm:text-5xl md:text-7xl font-bold mb-4"
    initial={{ opacity: 0, x: -50 }}
    whileInView={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.6, ease: "easeOut" }}
    viewport={{ once: false, amount: 0.5 }}
  >
    PHARMACONNECT
  </motion.h1>

  <motion.p
    className="text-base sm:text-lg md:text-xl font-medium max-w-xs sm:max-w-xl md:max-w-2xl"
    initial={{ opacity: 0, x: 50 }}
    whileInView={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
    viewport={{ once: false, amount: 0.5 }}
  >
    Revolutionizing Pharmacy Management with{" "}
    <br className="hidden sm:block" /> AI-Powered Automation
  </motion.p>
</section>


      {/* Info Section */}
     <section className="bg-white py-16 px-4">
  <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-16">
    <motion.div
      className="md:w-1/2 w-full"
      initial={{ opacity: 0, x: -100 }}
      whileInView={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      viewport={{ once: false, amount: 0.4 }}
    >
      <h2 className="text-2xl sm:text-3xl font-semibold text-black mb-4">
        Welcome to Pharmaconnect – Seamless Pharma Supply Management
      </h2>
      <p className="text-gray-700 text-base sm:text-lg md:text-xl leading-relaxed mb-4">
        PharmaConnect is an intelligent, cloud-based pharmacy business automation platform designed to revolutionize how pharmacies manage inventory, billing, vendor coordination, stock returns, and customer engagement.
      </p>
      <p className="text-gray-700 leading-relaxed text-base sm:text-lg md:text-xl">
        With AI-powered analytics, real-time stock tracking, and automated campaign management, PharmaConnect helps pharmacies reduce operational costs, prevent revenue loss, and scale effortlessly.
      </p>
    </motion.div>

    <motion.div
      className="md:w-1/2 w-full flex justify-center"
      initial={{ opacity: 0, x: 100 }}
      whileInView={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      viewport={{ once: false, amount: 0.4 }}
    >
      <img
        src={image551}
        alt="MedOps hospital solution"
        className="rounded-xl shadow-md w-full max-w-xs sm:max-w-md h-94 object-cover"
      />
    </motion.div>
  </div>
</section>


      {/* Smart Tools */}

      <section className="bg-[#d2e5e4] py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: false, amount: 0.3 }}
            className="text-3xl sm:text-4xl font-bold text-center text-[#3d302c] mb-12"
          >
            Leading Pharma, Trust PharmaConnect
          </motion.h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "Smart Inventory",
                desc: "Track and manage inventory with real-time AI-powered insights.",
              },
              {
                title: "Customer Engagement",
                desc: "Run targeted campaigns to boost loyalty and retention.",
              },
              {
                title: "Seamless Billing",
                desc: "Generate GST-compliant invoices and manage payments effortlessly.",
              },
              {
                title: "Analytics Dashboard",
                desc: "Visualize performance metrics to drive pharmacy growth.",
              },
              {
                title: "Vendor Coordination",
                desc: "Automated restocking and vendor alerts for efficient supply chain.",
              },
              {
                title: "Secure Cloud Access",
                desc: "Access data anytime with secure, scalable cloud architecture.",
              },
            ].map((item, index) => {
              const isEven = index % 2 === 0;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: isEven ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: false, amount: 0.3 }}
                  className="group bg-white hover:bg-[#3d302c] transition-colors duration-300 rounded-xl shadow-md p-6 text-center flex flex-col justify-center items-center h-[180px] sm:h-[200px]"
                >
                  <h3 className="text-lg sm:text-xl font-semibold text-[#3d302c] mb-2 group-hover:text-white transition-colors duration-300">
                    {item.title}
                  </h3>
                  <p className="text-black text-sm sm:text-base group-hover:text-white transition-colors duration-300">
                    {item.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Orbit and Features */}

      <section className="bg-[#d2e5e4] py-16 px-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-10">
          {/* Left Cards */}
          <div className="flex flex-col space-y-6 w-full sm:w-1/3">
            {[
              "AI-Driven Efficiency",
              "Customer-Centric Approach",
              "Insightful Analytics",
            ].map((title, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                viewport={{ once: false, amount: 0.3 }}
                className="bg-white text-black rounded-xl p-5 shadow-lg"
              >
                <h3 className="font-bold text-lg mb-2">{title}</h3>
                <p>
                  {
                    [
                      "Leverage artificial intelligence to optimize daily operations, reduce errors, and automate tasks.",
                      "Boost satisfaction with personalized campaigns, prescription reminders, and loyalty rewards.",
                      "Use powerful dashboards to gain actionable insights for smarter business decisions.",
                    ][i]
                  }
                </p>
              </motion.div>
            ))}
          </div>

          {/* Central Orbit */}
          <div className="relative flex items-center justify-center w-[280px] h-[280px] sm:w-[400px] sm:h-[400px] my-12 sm:my-0">
            <div className="w-32 h-32 sm:w-44 sm:h-44 bg-black rounded-full z-10"></div>
            <div className="absolute w-[280px] h-[280px] sm:w-[400px] sm:h-[400px] rounded-full border border-black"></div>
            <div className="absolute w-[200px] h-[200px] sm:w-[300px] sm:h-[300px] rounded-full border border-black"></div>

            {[
              "ai",
              "stock",
              "vendor",
              "billing",
              "engagement",
              "analytics",
            ].map((icon, i) => {
              const pos = [
                { top: "-6", left: "1/2", translateX: "-1/2" },
                { top: "16", right: "-4" },
                { bottom: "16", right: "-4" },
                { bottom: "-6", left: "1/2", translateX: "-1/2" },
                { bottom: "16", left: "-4" },
                { top: "16", left: "-4" },
              ][i];
              return (
                <div
                  key={icon}
                  className={`absolute ${pos.top ? `top-[${pos.top}]` : ""} ${
                    pos.bottom ? `bottom-[${pos.bottom}]` : ""
                  } ${pos.left ? `left-[${pos.left}]` : ""} ${
                    pos.right ? `right-[${pos.right}]` : ""
                  } ${
                    pos.translateX
                      ? `transform -translate-x-[${pos.translateX}]`
                      : ""
                  }`.trim()}
                >
                  <img
                    src={`/icons/${icon}.png`}
                    className="w-8 h-8 sm:w-12 sm:h-12"
                    alt={`${icon} Icon`}
                  />
                </div>
              );
            })}
          </div>

          {/* Right Cards */}
          <div className="flex flex-col space-y-6 w-full sm:w-1/3">
            {[
              "Real-Time Stock Updates",
              "Streamlined Vendor Management",
              "Compliance Made Easy",
            ].map((title, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                viewport={{ once: false, amount: 0.3 }}
                className="bg-white text-black rounded-xl p-5 shadow-lg"
              >
                <h3 className="font-bold text-lg mb-2">{title}</h3>
                <p>
                  {
                    [
                      "Monitor your stock levels in real-time, preventing overstocking or running out of critical medicines.",
                      "Centralize vendor interactions and get timely alerts for reorders and price drops.",
                      "Ensure easy adherence to regulatory standards with automated compliance checks and GST-ready billing.",
                    ][i]
                  }
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default PharmaConnect;
